import {
	axiosInstance,
	axiosJsonDirectInstance,
	axiosJsonInstance,
	axiosNoContentTypeInstance
} from "@/axios.instance";
import store from "@/store";
import { AxiosRequestConfig } from "axios";

/**
 * Regional interceptor service which can be applied/disabled when needed.
 */
class AxiosRegionalInterceptors {
	private axiosInterceptorId: number;
	private axiosJsonInterceptorId: number;
	private axiosJsonDirectInterceptorId: number;
	private axiosNoContentTypeInterceptorId: number;

	/**
	 * Intercepts axios requests and patches in the regional XSRF token. Also overwrites the baseURL if a regionURL is
	 * specified.
	 * @param config the axios request config.
	 */
	public regionalXsrfInterceptor(config: AxiosRequestConfig): AxiosRequestConfig {
		const regionsWithTokens: string[] = store.getters["regions/getRegionsWithXsrfTokens"];

		// If a region URL is specified on the config, overwrite the default baseURL.
		if (config.regionUrl) {
			config.baseURL = config.regionUrl;
		}

		// Check to see if this web request is hitting a remote region where the user has logged in
		// and received an XSRF token.
		if (regionsWithTokens.includes(config.baseURL)) {
			const regionToken = store.getters["regions/getXsrfToken"](config.baseURL);

			/**
			 * Bit of dirty trickery going on here. When axios sends a web request, it checks the browser
			 * cookies to see if there is an XSRF token cookie (for security reasons, this will always be
			 * the XSRF token for the local region), and applies it to the request header under the
			 * name specified by the xsrfHeaderName property.
			 *
			 * This will cause regional requests to be rejected, as the XSRF token for the local service
			 * will not be valid in the remote data service.
			 *
			 * So, we set the xsrfHeaderName property to something different, which means axios passes the
			 * local token under a different name.
			 *
			 * We then set the X-XSRF-TOKEN property on the header ourselves, using the regional token
			 * pulled from the store.
			 */

			config.xsrfHeaderName = "X-XSRF-TOKEN-LOCAL";
			config.headers["X-XSRF-TOKEN"] = regionToken;
		}

		return config;
	}

	/* istanbul ignore next */
	/**
	 * Applies the regional XSRF interceptor to axios.
	 */
	public applyInterceptors() {
		this.axiosInterceptorId = axiosInstance.interceptors.request.use(this.regionalXsrfInterceptor, undefined);
		this.axiosJsonInterceptorId = axiosJsonInstance.interceptors.request.use(
			this.regionalXsrfInterceptor,
			undefined
		);
		this.axiosJsonDirectInterceptorId = axiosJsonDirectInstance.interceptors.request.use(
			this.regionalXsrfInterceptor,
			undefined
		);
		this.axiosNoContentTypeInterceptorId = axiosNoContentTypeInstance.interceptors.request.use(
			this.regionalXsrfInterceptor,
			undefined
		);
	}

	/* istanbul ignore next */
	/**
	 * Removes the regional XSRF interceptor from axios.
	 */
	public removeInterceptors() {
		axiosInstance.interceptors.request.eject(this.axiosInterceptorId);
		axiosJsonInstance.interceptors.request.eject(this.axiosJsonInterceptorId);
		axiosJsonDirectInstance.interceptors.request.eject(this.axiosJsonDirectInterceptorId);
		axiosNoContentTypeInstance.interceptors.request.eject(this.axiosNoContentTypeInterceptorId);
	}
}

const regionalInterceptors = new AxiosRegionalInterceptors();
export default regionalInterceptors;
